import React, { useState, useRef } from 'react';
import posthog from 'posthog-js';
import { Icon } from './ui/Icon.jsx';
import { SkeletonBlock } from './Skeleton.jsx';
import T from '../locales/translations.js';
import { CATEGORY_TAXONOMY } from '../data/categories.js';
import { uid, fmt } from '../lib/utils.js';
import { autoCategory, catLabel } from '../data/categories.js';
import { CategoryIcon } from '../lib/icons.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProducts,
  useSaveProduct,
  useToggleActive,
  useUpdateProductField,
  useBulkUpdateProducts,
  useUpsertImport,
  useAutoTagProducts,
  useDeleteProduct,
  useProductVariants,
  useSaveVariant,
  useDeleteVariant,
} from '../hooks/useProducts.js';
import { supaFetch } from '../lib/supabase.js';
import { useCategories, useRenameCategory, useSetCategoryAr } from '../hooks/useCategories.js';
import { useVendors } from '../hooks/useVendors.js';
import { queryKeys } from '../lib/queryKeys.js';
import { uid as _uid } from '../lib/utils.js';
import ImageUploader from './ImageUploader.jsx';
import BulkImageUploader from './BulkImageUploader.jsx';
import { parseCSV, decodeCsvBuffer, downloadCsv } from '../lib/csv.js';
import {
  sortCatalogue,
  isOwnProduct,
  downloadCatalogueCsv,
  downloadCatalogueWorkbook,
} from '../lib/catalogueExport.js';

// ─── SHIMMER SKELETON ─────────────────────────────────────────────────────────
function SkeletonAdminCard() {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', display: 'flex', gap: 12, alignItems: 'center' }}>
      <SkeletonBlock width={52} height={52} radius={8} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SkeletonBlock width="70%" height={14} />
        <SkeletonBlock width="40%" height={11} />
        <SkeletonBlock width="25%" height={11} />
      </div>
      <SkeletonBlock width={60} height={26} radius={7} style={{ flexShrink: 0 }} />
    </div>
  );
}

// CSV parsing/decoding/download helpers now live in src/lib/csv.js (shared with
// the vendor portal batch upload).

// ─── IMPORT PREVIEW MODAL ─────────────────────────────────────────────────────
function ImportPreviewModal({ stage, lang, onConfirm, onCancel, busy }) {
  const { newRows, updateRows, errorRows } = stage;
  const ar = lang === 'ar';

  const Pill = ({ count, color, bg, label, icon }) => (
    <div style={{ flex: 1, minWidth: 130, background: bg, borderRadius: 12, padding: '18px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 34, fontWeight: 800, color, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 13, color, opacity: 0.75, marginTop: 4, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        {icon && <Icon name={icon} size={13} />} {label}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, padding: 32, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', direction: ar ? 'rtl' : 'ltr' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--sedra-sage-wash)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sedra-sage-ink)' }}><Icon name="ChartColumn" size={22} /></div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--sedra-forest)' }}>
              {ar ? 'معاينة الاستيراد' : 'Import Preview'}
            </div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
              {ar ? 'راجع التغييرات قبل التأكيد' : 'Review changes before confirming'}
            </div>
          </div>
        </div>

        {/* Stats pills */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          <Pill count={newRows.length}    color="var(--status-success)" bg="var(--status-success-bg)" icon="Sparkles" label={ar ? 'منتجات جديدة' : 'New Products'} />
          <Pill count={updateRows.length} color="#1e40af" bg="#dbeafe" icon="RefreshCw" label={ar ? 'سيتم تحديثها' : 'Will Update'} />
          <Pill count={errorRows.length}  color="var(--status-danger)" bg="var(--status-danger-bg)" icon="TriangleAlert" label={ar ? 'أخطاء' : 'Errors'} />
        </div>

        {/* Error list (collapsed, max 5) */}
        {errorRows.length > 0 && (
          <div style={{ background: 'var(--status-danger-bg)', border: '1px solid #fed7d7', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--status-danger)', marginBottom: 8 }}>
              {ar ? 'صفوف بها أخطاء (ستُتجاهل):' : 'Rows with errors (will be skipped):'}
            </div>
            {errorRows.slice(0, 5).map((r, i) => (
              <div key={i} style={{ fontSize: 12, color: '#742a2a', padding: '3px 0', borderTop: i > 0 ? '1px solid #fed7d7' : 'none' }}>
                <span style={{ fontWeight: 600 }}>{r.sku || (ar ? '(بدون SKU)' : '(no SKU)')}</span>
                {' — '}
                {!r.sku ? (ar ? 'رقم SKU مفقود' : 'Missing SKU') : (ar ? 'السعر مفقود أو صفر' : 'Missing or zero price')}
              </div>
            ))}
            {errorRows.length > 5 && (
              <div style={{ fontSize: 11, color: 'var(--status-danger)', marginTop: 6 }}>
                {ar ? `… و ${errorRows.length - 5} صفوف أخرى` : `… and ${errorRows.length - 5} more`}
              </div>
            )}
          </div>
        )}

        {/* Nothing to import guard */}
        {newRows.length === 0 && updateRows.length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', padding: '12px 0', fontSize: 14, marginBottom: 12 }}>
            {ar ? 'لا توجد منتجات صالحة للاستيراد.' : 'No valid products to import.'}
          </div>
        )}

        {/* Upsert note */}
        {updateRows.length > 0 && (
          <div style={{ background: 'var(--status-info-bg)', border: '1px solid #bfdbfe', borderRadius: 9, padding: '10px 14px', marginBottom: 20, fontSize: 12, color: '#1e40af', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <Icon name="Lightbulb" size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {ar
              ? `${updateRows.length} منتج موجود مسبقاً سيتم تحديث اسمه، سعره، وصوره بناءً على SKU.`
              : `${updateRows.length} existing product(s) will have their name, price, and images updated by SKU.`}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} disabled={busy}
            style={{ padding: '10px 22px', borderRadius: 9, border: '1.5px solid var(--sedra-line)', background: '#fff', color: '#555', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            {ar ? 'إلغاء' : 'Cancel'}
          </button>
          <button onClick={onConfirm} disabled={busy || (newRows.length === 0 && updateRows.length === 0)}
            style={{ padding: '10px 28px', borderRadius: 9, border: 'none',
              background: (newRows.length + updateRows.length) > 0 ? 'var(--sedra-olive)' : '#ccc',
              color: '#fff', cursor: (newRows.length + updateRows.length) > 0 ? 'pointer' : 'default',
              fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            {busy
              ? <><span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />{ar ? 'جارٍ الاستيراد…' : 'Importing…'}</>
              : (<><Icon name="CheckCircle2" size={16} /> {ar ? 'تأكيد وتنفيذ' : 'Confirm & Import'}</>)
            }
          </button>
        </div>
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
      </div>
    </div>
  );
}

// ─── VARIANT MANAGER ──────────────────────────────────────────────────────────
function VariantManager({ productId, lang }) {
  const { data: variants = [], isLoading } = useProductVariants(productId);
  const saveVariant   = useSaveVariant();
  const deleteVariant = useDeleteVariant();

  const blankVariant = () => ({
    id: _uid(), product_id: productId, label: '', sku: '',
    stock: 0, price_override: '', image_url: '', sort_order: variants.length,
  });

  const [editingId, setEditingId] = useState(null);
  const [vForm, setVForm]         = useState(null);

  const openNew  = () => { const v = blankVariant(); setVForm(v); setEditingId(v.id); };
  const openEdit = (v) => { setVForm({ ...v, price_override: v.price_override ?? '', image_url: v.image_url || '' }); setEditingId(v.id); };
  const close    = () => { setVForm(null); setEditingId(null); };

  const saveV = async () => {
    if (!vForm?.label) return;
    await saveVariant.mutateAsync({
      ...vForm,
      price_override: vForm.price_override !== '' ? parseFloat(vForm.price_override) : null,
    });
    close();
  };

  const delV = async (v) => {
    if (!window.confirm(lang === 'ar' ? 'حذف هذا الاختيار؟' : 'Delete this variant?')) return;
    await deleteVariant.mutateAsync({ id: v.id, product_id: v.product_id });
  };

  const inputS = { padding: '7px 10px', borderRadius: 6, border: '1.5px solid var(--sedra-line)', fontSize: 13, width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ gridColumn: '1/-1', marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--sedra-forest-soft)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="Package" size={14} /> {lang === 'ar' ? 'الاختيارات / الأحجام' : 'Product Variants'}
          {variants.length > 0 && (
            <span style={{ marginLeft: 6, background: 'var(--sedra-sage-ink)', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
              {variants.length}
            </span>
          )}
        </div>
        <button type="button" onClick={openNew}
          style={{ padding: '6px 14px', borderRadius: 7, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
          + {lang === 'ar' ? 'إضافة اختيار' : 'Add Variant'}
        </button>
      </div>

      {isLoading && <div style={{ color: '#aaa', fontSize: 13 }}>Loading…</div>}

      {variants.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {variants.map(v => (
            <div key={v.id} style={{ background: '#f7f5f0', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {v.image_url
                ? <img src={v.image_url} alt={v.label} style={{ width: 38, height: 38, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--sedra-line)' }} />
                : <div style={{ width: 38, height: 38, borderRadius: 6, background: '#ebe8e2', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}><Icon name="Camera" size={16} /></div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--sedra-forest)' }}>{v.label}</span>
                {v.sku && <span style={{ marginLeft: 8, fontSize: 12, color: '#aaa' }}>{v.sku}</span>}
                {v.price_override != null && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--sedra-sage-ink)', fontWeight: 600 }}>
                    {v.price_override} {lang === 'ar' ? 'ج.م.' : 'EGP'}
                  </span>
                )}
                <span style={{ marginLeft: 8, fontSize: 11, color: '#bbb' }}>
                  {lang === 'ar' ? 'مخزون:' : 'Stock:'} {v.stock ?? 0}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button type="button" onClick={() => openEdit(v)}
                  style={{ padding: '4px 10px', borderRadius: 6, background: '#6c8ebf', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex' }}><Icon name="Pencil" size={13} /></button>
                <button type="button" onClick={() => delV(v)}
                  style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--status-danger)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex' }}><Icon name="Trash2" size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {vForm && (
        <div style={{ background: '#fff', border: '1.5px solid var(--sedra-line)', borderRadius: 9, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--sedra-forest-soft)', marginBottom: 2 }}>
            {editingId && variants.find(v => v.id === editingId)
              ? (lang === 'ar' ? 'تعديل الاختيار' : 'Edit Variant')
              : (lang === 'ar' ? 'اختيار جديد' : 'New Variant')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>{lang === 'ar' ? 'الاسم *' : 'Label *'}</label>
              <input style={inputS} value={vForm.label} autoFocus onChange={e => setVForm(f => ({ ...f, label: e.target.value }))} placeholder={lang === 'ar' ? 'صغير / أزرق…' : 'Small / Blue…'} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>SKU</label>
              <input style={inputS} value={vForm.sku || ''} onChange={e => setVForm(f => ({ ...f, sku: e.target.value }))} placeholder="SKU-001-S" />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>{lang === 'ar' ? 'سعر خاص' : 'Price Override'}</label>
              <input style={inputS} type="number" min="0" step="0.01" value={vForm.price_override} onChange={e => setVForm(f => ({ ...f, price_override: e.target.value }))} placeholder={lang === 'ar' ? 'اختياري' : 'Optional'} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 3 }}>{lang === 'ar' ? 'المخزون' : 'Stock'}</label>
              <input style={inputS} type="number" min="0" value={vForm.stock || 0} onChange={e => setVForm(f => ({ ...f, stock: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 11, color: '#888', display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}><Icon name="Camera" size={12} /> {lang === 'ar' ? 'صورة هذا الاختيار (اختياري)' : 'Variant Image (optional)'}</label>
            <ImageUploader value={vForm.image_url || ''} onChange={url => setVForm(f => ({ ...f, image_url: url }))} lang={lang} nameHint={vForm.label || vForm.sku || 'variant'} compact />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={saveV} disabled={!vForm.label || saveVariant.isPending}
              style={{ padding: '7px 18px', borderRadius: 7, background: !vForm.label ? '#aaa' : 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: vForm.label ? 'pointer' : 'default', fontSize: 13, fontWeight: 700 }}>
              {saveVariant.isPending ? '…' : (lang === 'ar' ? 'حفظ' : 'Save Variant')}
            </button>
            <button type="button" onClick={close}
              style={{ padding: '7px 14px', borderRadius: 7, background: 'var(--sedra-line)', color: '#666', border: 'none', cursor: 'pointer', fontSize: 13 }}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {variants.length === 0 && !vForm && !isLoading && (
        <div style={{ fontSize: 12, color: '#bbb', padding: '6px 0' }}>
          {lang === 'ar' ? 'لا توجد اختيارات. أضف اختيارات لمنح المشترين خيارات مثل الحجم أو اللون.' : 'No variants yet. Add variants to give buyers size, colour, or pack options.'}
        </div>
      )}
    </div>
  );
}

// ─── PRODUCTS ─────────────────────────────────────────────────────────────────
export default function Products({ lang }) {
  const t = T[lang];
  const isMobile = useIsMobile();

  // ── data + mutations ──
  const queryClient    = useQueryClient();
  const { data: products = [], isLoading } = useProducts();
  const saveProduct    = useSaveProduct();
  const toggleActive   = useToggleActive();    // soft-delete / restore
  // ── Category authority ────────────────────────────────────────────────────
  const { data: dbCategories = [] } = useCategories();
  // Vendor rows name the supplier of every non-Sedra-Home product in the
  // .xlsx extract. Cached by TanStack and shared with the Vendors screen, so
  // opening Products costs no extra round-trip when it is already warm.
  const { data: vendors = [] } = useVendors();
  const renameCat  = useRenameCategory();
  const setCatAr   = useSetCategoryAr();
  const deleteProduct  = useDeleteProduct();   // hard-delete (inactive only)
  const updateField    = useUpdateProductField();
  const bulkUpdate     = useBulkUpdateProducts();
  const upsertImport   = useUpsertImport();
  const autoTagMut     = useAutoTagProducts();

  // ── local UI state ──
  const [form, setForm]               = useState(null);
  const [msg, setMsg]                 = useState('');
  const [search, setSearch]           = useState('');
  const [catFilter, setCatFilter]     = useState('all');
  const [busy, setBusy]               = useState(false);
  const [editPriceId, setEditPriceId] = useState(null);
  const [editPrice, setEditPrice]     = useState('');
  const [editStockId, setEditStockId] = useState(null);
  const [editStock, setEditStock]     = useState('');
  const [collapsed, setCollapsed]     = useState({});
  const [sortBy, setSortBy]           = useState('newest');
  // Merchandising: float Sedra Home (first-party) products above vendor ones.
  // Defaults ON to match the storefront's `catalogue_own_first` setting, so the
  // admin list — and any extract taken from it — reads in the same order buyers
  // see. A tier on top of sortBy, never a replacement for it.
  const [shFirst, setShFirst]         = useState(true);
  const [xlsxBusy, setXlsxBusy]       = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCat, setBulkCat]         = useState('');
  const [showCatMgr, setShowCatMgr]   = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [activeTab, setActiveTab]     = useState('products');
  // ── Category management state ─────────────────────────────────────────────
  const [catEditTarget, setCatEditTarget] = useState(null);
  const [catEditNewEn,  setCatEditNewEn]  = useState('');
  const [catEditNewAr,  setCatEditNewAr]  = useState('');
  const [catEditMsg,    setCatEditMsg]    = useState('');

  // ── Import pipeline state ──
  // importStage: null | { newRows, updateRows, errorRows }
  const [importStage, setImportStage] = useState(null);
  const [importBusy, setImportBusy]   = useState(false);
  // lastImport: null | { batchId, newIds[], updateCount }  — for undo
  const [lastImport, setLastImport]   = useState(null);

  const blank = { id: uid(), name_en: '', name_ar: '', sku: '', category: '', category_ar: '', price: '', min_qty: 1, carton_qty: 1, image: '', images: [], description: '', description_ar: '' };

  const toast = (m, duration = 4000) => { setMsg(m); setTimeout(() => setMsg(''), duration); };

  // ── save (add / edit) ──
  const save = async () => {
    if (!form.name_en || !form.sku || !form.price) return;
    const { _isNew, ...cleanForm } = form;
    try {
      await saveProduct.mutateAsync({ product: cleanForm, isNew: !!_isNew });
      posthog.capture('product_saved', {
        product_id: cleanForm.id,
        operation: _isNew ? 'create' : 'update',
        category: cleanForm.category || 'uncategorized',
        has_variants: !_isNew,
      });
      toast(t.productSaved); setForm(null);
    } catch (e) {
      posthog.captureException(e, { flow: 'admin_product_save' });
      toast('Error: ' + e.message);
    }
  };

  // ── soft-delete / restore single ──
  const del = async (p) => {
    const isActive = p.is_active !== false;
    if (isActive) {
      if (!window.confirm(lang === 'ar' ? 'إلغاء تنشيط هذا المنتج؟ (لن يظهر للمشترين)' : 'Deactivate this product? (Hidden from buyers)')) return;
    }
    try {
      await toggleActive.mutateAsync({ id: p.id, active: !isActive });
      toast(isActive
        ? (lang === 'ar' ? 'تم إلغاء تنشيط المنتج' : 'Product deactivated')
        : (lang === 'ar' ? 'تم استعادة المنتج' : 'Product restored'));
    } catch (e) { toast('Error: ' + e.message); }
  };

  // ── hard-delete (only available on inactive products) ──
  const hardDel = async (p) => {
    if (!window.confirm(lang === 'ar'
      ? `حذف نهائي لـ "${p.name_ar || p.name_en}"؟ لا يمكن التراجع.`
      : `Permanently delete "${p.name_en}"? This cannot be undone.`)) return;
    try {
      await deleteProduct.mutateAsync(p.id);
      toast(lang === 'ar' ? 'تم حذف المنتج نهائياً' : 'Product permanently deleted');
    } catch (e) { toast('Error: ' + e.message); }
  };

  // ── CSV → staging (no DB write yet) ──
  const handleCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    // Try UTF-8 first (strict); fall back to Windows-1256 for Arabic Excel exports
    const buf = await file.arrayBuffer();
    const parsed = parseCSV(decodeCsvBuffer(buf));
    if (!parsed.length) { toast(lang === 'ar' ? 'لم يتم العثور على صفوف صالحة' : 'No valid rows found in CSV'); return; }

    // Build a SKU → product map from the current cache for O(1) lookups
    const skuMap = {};
    (products || []).forEach(p => { if (p.sku) skuMap[p.sku] = p; });

    const newRows = [], updateRows = [], errorRows = [];
    for (const r of parsed) {
      const price = parseFloat(r.price);
      if (!r.sku || isNaN(price) || price <= 0) {
        errorRows.push(r);
        continue;
      }
      const row = {
        id:          skuMap[r.sku]?.id || uid(),   // reuse existing id for updates
        name_en:     r.name_en    || '',
        name_ar:     r.name_ar    || '',
        sku:         r.sku,
        category:    r.category   || '',
        category_ar: r.category_ar || '',
        price,
        min_qty:     parseInt(r.min_qty)    || 1,
        carton_qty:  parseInt(r.carton_qty) || 1,
        image:       r.image_url  || r.image || '',
        images:      r.images ? r.images.split('|').map(s => s.trim()).filter(Boolean) : [],
        created_at:  skuMap[r.sku]?.created_at || new Date().toISOString(),
        is_active:   true,
      };
      if (skuMap[r.sku]) { updateRows.push(row); } else { newRows.push(row); }
    }

    setImportStage({ newRows, updateRows, errorRows });
  };

  // ── confirm & execute import ──
  const confirmImport = async () => {
    if (!importStage) return;
    const { newRows, updateRows } = importStage;
    const allRows = [...newRows, ...updateRows];
    if (!allRows.length) { setImportStage(null); return; }

    const batchId = 'batch_' + Date.now();
    setImportBusy(true);
    try {
      await upsertImport.mutateAsync({ rows: allRows, batchId });

      setLastImport({
        batchId,
        newIds:      newRows.map(r => r.id),
        updateCount: updateRows.length,
      });

      setImportStage(null);
      toast(
        lang === 'ar'
          ? `${newRows.length} منتج جديد، ${updateRows.length} تم تحديثه`
          : `${newRows.length} added, ${updateRows.length} updated`,
        5000
      );
    } catch (e) { toast('Error: ' + e.message); }
    setImportBusy(false);
  };

  // ── undo last import (hard-delete newly inserted products only) ──
  const revertLastImport = async () => {
    if (!lastImport) return;
    const { newIds, updateCount, batchId } = lastImport;
    const msg = updateCount > 0
      ? (lang === 'ar'
          ? `سيتم حذف ${newIds.length} منتج جديد نهائياً.\n${updateCount} منتج تم تحديثه لا يمكن التراجع عنه تلقائياً.`
          : `${newIds.length} newly-added product(s) will be permanently deleted.\n${updateCount} updated product(s) cannot be auto-reverted — fix those manually via CSV.`)
      : (lang === 'ar'
          ? `حذف ${newIds.length} منتج تم استيراده من الدُفعة الأخيرة نهائياً؟`
          : `Permanently delete ${newIds.length} product(s) from the last import batch?`);

    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      for (const id of newIds) {
        await supaFetch('DELETE', 'products', null, `?id=eq.${id}`);
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.productsRoot() });
      setLastImport(null);
      toast(lang === 'ar' ? 'UNDO:تم التراجع عن الاستيراد' : 'UNDO:Import reverted', 5000);
    } catch (e) { toast('Error: ' + e.message); }
    setBusy(false);
  };

  // ── download CSV template ──
  const downloadTemplate = () => {
    const csv = 'name_en,name_ar,sku,category,category_ar,price,min_qty,carton_qty,image_url,images\nExample Product,اسم المنتج,SKU001,cookware,,150,6,12,https://example.com/cover.jpg,https://example.com/img2.jpg|https://example.com/img3.jpg';
    downloadCsv('sedra_products_template.csv', csv);
  };

  // ── bulk category assign ──
  const applyBulkCat = async () => {
    if (!bulkCat || selectedIds.size === 0) return;
    try {
      await bulkUpdate.mutateAsync({ ids: selectedIds, patch: { category: bulkCat } });
      setSelectedIds(new Set()); setBulkCat('');
      toast('Category updated for ' + selectedIds.size + ' products');
    } catch (err) { toast('Error: ' + err.message); }
  };

  // ── bulk soft-delete ──
  const bulkDeactivate = async () => {
    const n = selectedIds.size;
    if (!window.confirm(lang === 'ar' ? `إلغاء تنشيط ${n} منتج؟ (لن تظهر للمشترين)` : `Deactivate ${n} products? (Hidden from buyers)`)) return;
    setBusy(true);
    try {
      await bulkUpdate.mutateAsync({ ids: selectedIds, patch: { is_active: false } });
      setSelectedIds(new Set()); setBulkCat('');
      toast(n + (lang === 'ar' ? ' منتجات تم إلغاء تنشيطها' : ' products deactivated'));
    } catch (err) { toast('Error: ' + err.message); }
    setBusy(false);
  };

  // ── inline price save ──
  const savePrice = async (id) => {
    const val = parseFloat(editPrice);
    if (!isNaN(val) && val > 0) {
      try { await updateField.mutateAsync({ id, patch: { price: val } }); toast('Price updated'); }
      catch (e) { toast('Error: ' + e.message); }
    }
    setEditPriceId(null);
  };

  // ── inline stock save ──
  const saveStock = async (id) => {
    const val = parseInt(editStock, 10);
    if (!isNaN(val) && val >= 0) {
      try { await updateField.mutateAsync({ id, patch: { stock: val } }); toast(lang === 'ar' ? 'تم تحديث المخزون' : 'Stock updated'); }
      catch (e) { toast('Error: ' + e.message); }
    }
    setEditStockId(null);
  };

  // ── export the catalogue ──
  // Always the WHOLE catalogue — inactive rows and every category included,
  // whatever the screen is currently filtered to. "Export" here means "take a
  // copy of the shop", not "save this view"; an admin who exports mid-search
  // should not silently get a 12-row file. The chosen ORDER does carry over,
  // so Sedra Home products lead the file while the toggle is on.
  const catalogueForExport = () => sortCatalogue(products || [], { shFirst, sortBy });
  const exportOpts = () => ({ lang, vendors, shFirst });

  const exportCsv = () => {
    const n = downloadCatalogueCsv(catalogueForExport(), exportOpts());
    posthog.capture('catalogue_exported', { format: 'csv', products: n, sh_first: shFirst });
    toast(lang === 'ar' ? `تم تصدير ${n} منتج إلى CSV` : `Exported ${n} products to CSV`);
  };

  // ExcelJS is ~900 KB and is only fetched on this first click, so the button
  // has to say it is working — on a slow connection the file is seconds away.
  const exportXlsx = async () => {
    if (xlsxBusy) return;
    setXlsxBusy(true);
    try {
      const n = await downloadCatalogueWorkbook(catalogueForExport(), exportOpts());
      posthog.capture('catalogue_exported', { format: 'xlsx', products: n, sh_first: shFirst });
      toast(lang === 'ar' ? `تم تصدير ${n} منتج إلى Excel` : `Exported ${n} products to Excel`);
    } catch (e) {
      posthog.captureException(e, { flow: 'admin_catalogue_xlsx_export' });
      toast('Error: ' + e.message);
    }
    setXlsxBusy(false);
  };

  // ── auto-tag uncategorised ──
  const autoTagAll = async () => {
    const uncatted = (products || []).filter(p => !p.category || p.category === 'other');
    if (!uncatted.length) { toast('All products already categorised!'); return; }
    try {
      await autoTagMut.mutateAsync(uncatted.map(p => ({ id: p.id, category: autoCategory(p.name_en, '', '') })));
      toast('Auto-categorised ' + uncatted.length + ' products');
    } catch (err) { toast('Error: ' + err.message); }
  };

  // ── derived display data ──
  const displayProducts = showInactive ? (products || []) : (products || []).filter(p => p.is_active !== false);
  const usedIds = Array.from(new Set(displayProducts.map(p => p.category).filter(Boolean)));
  // Show ALL categories that exist in products: taxonomy ones first (in taxonomy order), then any custom ones.
  const taxonomyIds = CATEGORY_TAXONOMY.map(c => c.id);
  const allCategories = [
    'all',
    ...taxonomyIds.filter(id => usedIds.includes(id)),   // known — in predefined order
    ...usedIds.filter(id => !taxonomyIds.includes(id)),  // custom/unknown — appended after
  ];

  // One comparator for the list and for both exports — see lib/catalogueExport.js.
  const filtered = sortCatalogue(
    displayProducts.filter(p =>
      (catFilter === 'all' || (p.category || '—') === catFilter) &&
      (p.name_en?.toLowerCase().includes(search.toLowerCase()) ||
       p.name_ar?.includes(search) || p.sku?.toLowerCase().includes(search.toLowerCase()))
    ),
    { shFirst, sortBy },
  );

  const grouped = filtered.reduce((acc, p) => {
    const key = p.category || '—'; if (!acc[key]) acc[key] = []; acc[key].push(p); return acc;
  }, {});

  const inactiveCount = (products || []).filter(p => p.is_active === false).length;
  const vendorCount   = (products || []).filter(p => !isOwnProduct(p)).length;

  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const btnStyle = (color = 'var(--sedra-olive)') => ({ padding: '8px 16px', borderRadius: 7, background: color, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 });

  if (isLoading) return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[0,1,2,3,4,5].map(i => <SkeletonAdminCard key={i} />)}
      </div>
    </div>
  );

  return (
    <div>
      {/* ── Import Preview Modal ── */}
      {importStage && (
        <ImportPreviewModal
          stage={importStage}
          lang={lang}
          busy={importBusy}
          onConfirm={confirmImport}
          onCancel={() => setImportStage(null)}
        />
      )}

      {/* ── Tab bar + toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 4, background: 'var(--sedra-sage-wash)', borderRadius: 10, padding: 4 }}>
          {[
            { key: 'products',     icon: 'Package', label: lang === 'ar' ? 'المنتجات'       : 'Products' },
            { key: 'bulk-upload',  icon: 'Image',    label: lang === 'ar' ? 'رفع الصور المجمع' : 'Bulk Image Upload' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: '7px 16px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: activeTab === tab.key ? 'var(--sedra-olive)' : 'transparent',
                color:      activeTab === tab.key ? '#fff'    : 'var(--sedra-sage-ink)',
                transition: 'background 0.15s, color 0.15s', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={tab.icon} size={14} /> {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'products' && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Undo last import */}
            {lastImport && lastImport.newIds.length > 0 && (
              <button onClick={revertLastImport} disabled={busy}
                style={{ padding: '8px 14px', borderRadius: 7, background: '#fff7ed', color: '#c2410c', border: '1.5px solid #fed7aa', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="Undo2" size={13} /> {lang === 'ar' ? `تراجع (${lastImport.newIds.length} جديد)` : `Undo Import (${lastImport.newIds.length} new)`}
              </button>
            )}
            <button onClick={downloadTemplate}  style={btnStyle('#888')}>{t.downloadTemplate}</button>
            <button onClick={exportCsv}
              title={lang === 'ar' ? 'ملف للتعديل وإعادة الاستيراد' : 'Editable file you can import back'}
              style={{ ...btnStyle('#6b7280'), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Download" size={13} /> {lang === 'ar' ? 'تصدير CSV' : 'Export CSV'}
            </button>
            {xlsxBusy && <style>{'@keyframes sdra-spin{to{transform:rotate(360deg)}}'}</style>}
            <button onClick={exportXlsx} disabled={xlsxBusy}
              title={lang === 'ar' ? 'كتالوج كامل منسّق للقراءة في Excel' : 'Full catalogue, formatted for reading in Excel'}
              style={{ ...btnStyle('#1d6f42'), display: 'inline-flex', alignItems: 'center', gap: 6, opacity: xlsxBusy ? 0.6 : 1, cursor: xlsxBusy ? 'wait' : 'pointer' }}>
              <Icon name={xlsxBusy ? 'Loader2' : 'FileSpreadsheet'} size={13} style={xlsxBusy ? { animation: 'sdra-spin 0.9s linear infinite' } : undefined} />
              {xlsxBusy
                ? (lang === 'ar' ? 'جارٍ التصدير…' : 'Exporting…')
                : (lang === 'ar' ? 'تصدير Excel' : 'Export Excel')}
            </button>
            <label style={{ ...btnStyle('#6c8ebf'), display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <Icon name="FolderOpen" size={13} /> {lang === 'ar' ? 'استيراد CSV' : 'Import CSV'}
              <input type="file" accept=".csv" onChange={handleCSV} style={{ display: 'none' }} />
            </label>
            <button onClick={() => setForm({ ...blank, _isNew: true })} style={btnStyle()}>{t.addProduct}</button>
          </div>
        )}
      </div>

      {/* ── Bulk Image Upload tab ── */}
      {activeTab === 'bulk-upload' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ margin: '0 0 6px', color: 'var(--sedra-forest-soft)', fontSize: 17, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="Image" size={16} /> {lang === 'ar' ? 'رفع الصور المجمع' : 'Bulk Image Upload'}
          </h3>
          <p style={{ margin: '0 0 18px', fontSize: 13, color: '#888' }}>
            {lang === 'ar'
              ? 'ارفع صورك مرة واحدة، ثم انسخ الروابط وضعها في عمود image_url في ملف CSV الخاص بك.'
              : 'Upload all your product images at once, then copy the resulting URLs into the image_url column of your CSV sheet before importing.'}
          </p>
          <BulkImageUploader lang={lang} />
        </div>
      )}

      {/* ── Toast ── */}
      {msg && (
        <div style={{ position: 'fixed', top: 20, right: lang === 'ar' ? 'auto' : 20, left: lang === 'ar' ? 20 : 'auto', zIndex: 9999,
          padding: '13px 22px', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10,
          background: msg.startsWith('Error') ? 'var(--status-danger)' : 'var(--sedra-forest)', color: '#fff', fontSize: 14, fontWeight: 600,
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)', animation: 'sdra-toastIn 0.3s cubic-bezier(0.22,1,0.36,1)' }}>
          <style>{'@keyframes sdra-toastIn{from{opacity:0;transform:translateY(-14px) scale(0.95)}to{opacity:1;transform:translateY(0) scale(1)}}'}</style>
          <Icon name={msg.startsWith('Error') ? 'TriangleAlert' : (msg.startsWith('UNDO:') ? 'Undo2' : 'CheckCircle2')} size={18} />
          <span>{msg.replace(/^UNDO:/, '')}</span>
        </div>
      )}

      {/* ── Product form (add / edit) ── */}
      {form && (
        <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px' }}>{form.name_en ? t.editProduct : t.addProduct}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['name_en', t.nameEn], ['name_ar', t.nameAr], ['sku', t.sku], ['price', t.price], ['min_qty', t.minQty], ['carton_qty', t.cartonQty]].map(([key, label]) => (
              <div key={key}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
                <input style={inputStyle} value={form[key] ?? ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  type={['price','min_qty','carton_qty'].includes(key) ? 'number' : 'text'}
                  min={['carton_qty','min_qty'].includes(key) ? 1 : undefined} />
              </div>
            ))}
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{t.category}</label>
              {/* Dynamic dropdown from DB — shows ALL existing categories with counts */}
              <select style={{ ...inputStyle, cursor: 'pointer' }}
                value={dbCategories.find(c => c.en === form.category) ? form.category : (form.category ? '__new__' : '')}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '__new__') {
                    setForm(f => ({ ...f, category: '__new__', category_ar: '' }));
                  } else {
                    const found = dbCategories.find(c => c.en === val);
                    setForm(f => ({ ...f, category: val, category_ar: found?.ar || f.category_ar || '' }));
                  }
                }}>
                <option value="">-- {lang === 'ar' ? 'اختر الفئة' : 'Select category'} --</option>
                {dbCategories.map(c => (
                  <option key={c.en} value={c.en}>
                    {lang === 'ar' ? (c.ar || c.en) : c.en} ({c.product_count})
                  </option>
                ))}
                <option value="__new__">{lang === 'ar' ? 'فئة جديدة…' : 'New category…'}</option>
              </select>
              {(form.category === '__new__' || (form.category && !dbCategories.find(c => c.en === form.category))) && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <input autoFocus style={{ ...inputStyle }}
                    placeholder={lang === 'ar' ? 'اسم الفئة بالإنجليزية…' : 'Category name (English / key)…'}
                    value={form.category === '__new__' ? '' : form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value || '__new__' }))} />
                  <input style={{ ...inputStyle, direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif" }}
                    placeholder="اسم الفئة بالعربية…"
                    value={form.category_ar || ''}
                    onChange={e => setForm(f => ({ ...f, category_ar: e.target.value }))} />
                </div>
              )}
              {form.category && form.category !== '__new__' && dbCategories.find(c => c.en === form.category) && !dbCategories.find(c => c.en === form.category)?.ar && (
                <div style={{ marginTop: 6 }}>
                  <input style={{ ...inputStyle, direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif" }}
                    placeholder="أضف ترجمة عربية لهذه الفئة (اختياري)…"
                    value={form.category_ar || ''}
                    onChange={e => setForm(f => ({ ...f, category_ar: e.target.value }))} />
                </div>
              )}
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <ImageUploader value={form.image || ''} onChange={url => setForm(f => ({ ...f, image: url }))} lang={lang} nameHint={form.name_en || form.sku || 'product'} label={`${t.imageUrl} (${lang === 'ar' ? 'الصورة الرئيسية' : 'Cover / Main Image'})`} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <Icon name="Camera" size={13} /> {lang === 'ar' ? 'صور إضافية (معرض الصور)' : 'Extra Images (Gallery)'}
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(Array.isArray(form.images) ? form.images : []).map((url, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <ImageUploader
                        value={url}
                        onChange={newUrl => setForm(f => {
                          const imgs = [...(Array.isArray(f.images) ? f.images : [])];
                          imgs[idx] = newUrl;
                          return { ...f, images: imgs };
                        })}
                        lang={lang}
                        nameHint={`${form.name_en || form.sku || 'product'}-${idx + 1}`}
                        compact
                      />
                    </div>
                    <button type="button" onClick={() => setForm(f => {
                        const imgs = [...(Array.isArray(f.images) ? f.images : [])];
                        imgs.splice(idx, 1);
                        return { ...f, images: imgs };
                      })}
                      style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--status-danger-bg)', color: 'var(--status-danger)', border: 'none', cursor: 'pointer', flexShrink: 0, marginTop: 4, display: 'flex' }}>
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, images: [...(Array.isArray(f.images) ? f.images : []), ''] }))}
                  style={{ padding: '7px 14px', borderRadius: 7, border: '1.5px dashed var(--sedra-sage)', background: 'var(--sedra-sage-wash)', color: 'var(--sedra-sage-ink)', cursor: 'pointer', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
                  + {lang === 'ar' ? 'إضافة صورة للمعرض' : 'Add Gallery Image'}
                </button>
              </div>
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{lang === 'ar' ? 'الوصف' : 'Description'}</label>
              <textarea rows={4} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={lang === 'ar' ? 'وصف المنتج...' : 'Product description...'} />
            </div>
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>الوصف (عربي) — Arabic Description</label>
              <textarea rows={3} dir="rtl" style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Tajawal',sans-serif", direction: 'rtl', textAlign: 'right' }} value={form.description_ar || ''} onChange={e => setForm(f => ({ ...f, description_ar: e.target.value }))} placeholder="وصف المنتج بالعربية..." />
            </div>
          </div>

          {form.id && !form._isNew && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1.5px solid var(--sedra-line)' }}>
              <VariantManager productId={form.id} lang={lang} />
            </div>
          )}
          {form._isNew && (
            <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, background: '#fffbf0', border: '1px solid #fcd34d', fontSize: 13, color: 'var(--status-warn-dark)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              <Icon name="Lightbulb" size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {lang === 'ar' ? 'احفظ المنتج أولاً، ثم يمكنك إضافة اختيارات.' : 'Save the product first, then you can add variants.'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={save} style={btnStyle()}>{t.save}</button>
            <button onClick={() => setForm(null)} style={btnStyle('#888')}>{t.cancel}</button>
          </div>
        </div>
      )}

      {/* ── Search + filter + sort ── */}
      {activeTab === 'products' && (
        <>
          {/* ── Search + sort controls ── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input placeholder={t.searchProducts} value={search} onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, flex: 1, minWidth: 180 }} />
            <button onClick={() => setSortBy(s => s === 'newest' ? 'az' : 'newest')}
              style={{ padding: '9px 14px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--sedra-forest-soft)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name={sortBy === 'newest' ? 'Clock' : 'ArrowDownAZ'} size={14} /> {sortBy === 'newest' ? 'Newest' : 'A–Z'}
            </button>
            {/* Sedra Home first — a tier on top of the sort above, not a replacement.
                Mirrors the storefront's "My Products First" setting, and the CSV /
                Excel extracts follow whatever this button says. */}
            <button onClick={() => setShFirst(v => !v)}
              title={lang === 'ar'
                ? (vendorCount
                    ? `${vendorCount} منتج من البائعين ينزل أسفل منتجات سدرة هوم`
                    : 'لا توجد منتجات بائعين حالياً — كل الكتالوج من سدرة هوم')
                : (vendorCount
                    ? `${vendorCount} vendor product${vendorCount === 1 ? '' : 's'} drop below the Sedra Home ones`
                    : 'No vendor products yet — the whole catalogue is Sedra Home')}
              style={{ padding: '9px 14px', borderRadius: 7, border: '1.5px solid ' + (shFirst ? 'var(--sedra-olive)' : 'var(--sedra-line)'), background: shFirst ? 'var(--sedra-olive)' : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: shFirst ? '#fff' : 'var(--sedra-forest-soft)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Home" size={14} /> {lang === 'ar' ? 'سدرة هوم أولاً' : 'Sedra Home first'}
            </button>
            {/* Collapse / Expand All */}
            {(() => {
              const catKeys = Object.keys(grouped);
              const allCollapsed = catKeys.length > 0 && catKeys.every(k => collapsed[k] === true);
              return (
                <button
                  onClick={() => setCollapsed(allCollapsed ? {} : Object.fromEntries(catKeys.map(k => [k, true])))}
                  style={{ padding: '9px 14px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--sedra-forest-soft)', whiteSpace: 'nowrap' }}>
                  {allCollapsed
                    ? (lang === 'ar' ? '▼ توسيع الكل' : '▼ Expand All')
                    : (lang === 'ar' ? '▲ طي الكل'   : '▲ Collapse All')}
                </button>
              );
            })()}
            <button onClick={() => setShowCatMgr(s => !s)}
              style={{ padding: '9px 14px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', background: showCatMgr ? 'var(--sedra-olive)' : '#fff', color: showCatMgr ? '#fff' : 'var(--sedra-forest-soft)', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Tag" size={14} /> {lang === 'ar' ? 'الفئات' : 'Categories'}
            </button>
            {/* Show / hide inactive toggle */}
            {inactiveCount > 0 && (
              <button onClick={() => setShowInactive(s => !s)}
                style={{ padding: '9px 14px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', background: showInactive ? 'var(--status-warn-bg)' : '#fff', color: 'var(--status-warn-dark)', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name={showInactive ? 'EyeOff' : 'Eye'} size={14} /> {showInactive
                  ? (lang === 'ar' ? 'إخفاء غير النشطة' : 'Hide inactive')
                  : (lang === 'ar' ? `${inactiveCount} غير نشط` : `${inactiveCount} inactive`)}
              </button>
            )}
          </div>

          {/* ── Horizontal category nav bar ── */}
          <div style={{ overflowX: 'auto', marginBottom: 14, WebkitOverflowScrolling: 'touch' }}>
            <div style={{ display: 'flex', gap: 6, paddingBottom: 4 }}>
              {allCategories.map(c => {
                const active = catFilter === c;
                const cnt = c === 'all'
                  ? displayProducts.length
                  : displayProducts.filter(p => p.category === c).length;
                const label = c === 'all'
                  ? (lang === 'ar' ? 'الكل' : 'All')
                  : (<><CategoryIcon category={c} size={13} style={{ marginInlineEnd: 4 }} /> {catLabel(c, lang)}</>);
                return (
                  <button
                    key={c}
                    onClick={() => setCatFilter(c)}
                    style={{
                      flexShrink: 0,
                      padding: '7px 13px',
                      borderRadius: 20,
                      border: '1.5px solid ' + (active ? 'var(--sedra-forest-soft)' : 'var(--sedra-line)'),
                      background: active ? 'var(--sedra-olive)' : '#fff',
                      color: active ? '#fff' : 'var(--sedra-forest-soft)',
                      fontSize: 13,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s',
                      lineHeight: 1.3,
                    }}
                  >
                    {label}
                    <span style={{
                      marginLeft: 5,
                      fontSize: 11,
                      background: active ? 'rgba(255,255,255,0.25)' : 'var(--sedra-sage-wash)',
                      color: active ? '#fff' : '#888',
                      borderRadius: 10,
                      padding: '1px 6px',
                      fontWeight: 600,
                    }}>
                      {cnt}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {showCatMgr && (
            <div style={{ background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <h4 style={{ margin: 0, color: 'var(--sedra-forest-soft)', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Tag" size={16} /> {lang === 'ar' ? 'إدارة الفئات' : 'Manage Categories'}</h4>
                <button onClick={autoTagAll} style={{ padding: '7px 14px', borderRadius: 7, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Icon name="Brain" size={14} /> {lang === 'ar' ? 'تصنيف تلقائي للكل' : 'Auto-tag All Uncategorised'}
                </button>
              </div>
              {catEditMsg && (
                <div style={{ background: catEditMsg.startsWith('OK:') ? '#f0fdf4' : 'var(--status-danger-bg)', border: '1px solid ' + (catEditMsg.startsWith('OK:') ? '#bbf7d0' : '#fecaca'), borderRadius: 7, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: catEditMsg.startsWith('OK:') ? 'var(--status-success)' : 'var(--status-danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Icon name={catEditMsg.startsWith('OK:') ? 'CheckCircle2' : 'XCircle'} size={14} /> {catEditMsg.replace(/^(OK:|ERR:)/, '')}
                </div>
              )}
              {catEditTarget && (
                <div style={{ background: 'var(--sedra-sage-wash)', border: '1.5px solid #c8dab0', borderRadius: 9, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sedra-forest-soft)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="Pencil" size={12} /> {lang === 'ar' ? `تحرير: ${catEditTarget.en}` : `Editing: ${catEditTarget.en}`}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <div>
                      <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 4 }}>{lang === 'ar' ? 'الاسم (إنجليزي)' : 'Name (English / key)'}</label>
                      <input value={catEditNewEn} onChange={e => setCatEditNewEn(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', fontSize: 13, boxSizing: 'border-box' }} placeholder={catEditTarget.en} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 4 }}>{lang === 'ar' ? 'الاسم بالعربية' : 'Arabic name'}</label>
                      <input value={catEditNewAr} onChange={e => setCatEditNewAr(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', fontSize: 13, direction: 'rtl', textAlign: 'right', fontFamily: "'Tajawal',sans-serif", boxSizing: 'border-box' }} placeholder={catEditTarget.ar || 'الاسم بالعربية…'} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 10, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                    <Icon name="Lightbulb" size={12} style={{ flexShrink: 0, marginTop: 1 }} /> {lang === 'ar' ? 'لدمج فئتين: اكتب اسم الفئة الهدف في حقل الاسم الإنجليزي.' : 'To merge: type the target category name in English field — all products move to it.'}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button disabled={renameCat.isPending || setCatAr.isPending} onClick={async () => {
                      setCatEditMsg('');
                      const newEn = catEditNewEn.trim() || catEditTarget.en;
                      const newAr = catEditNewAr.trim() || null;
                      try {
                        if (newEn !== catEditTarget.en) {
                          const { data } = await renameCat.mutateAsync({ oldEn: catEditTarget.en, newEn, newAr });
                          const count = Array.isArray(data) ? data[0] : data;
                          setCatEditMsg(`OK:${count} ${lang === 'ar' ? 'منتج تم تحديثه' : 'products updated'}`);
                        } else if (newAr) {
                          const { data } = await setCatAr.mutateAsync({ en: catEditTarget.en, ar: newAr });
                          const count = Array.isArray(data) ? data[0] : data;
                          setCatEditMsg(`OK:${count} ${lang === 'ar' ? 'منتج تم تحديثه' : 'products updated'}`);
                        }
                        setCatEditTarget(null); setCatEditNewEn(''); setCatEditNewAr('');
                      } catch (err) { setCatEditMsg('ERR:' + err.message); }
                    }} style={{ padding: '8px 18px', borderRadius: 7, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                      {renameCat.isPending || setCatAr.isPending ? '…' : (lang === 'ar' ? 'حفظ' : 'Save')}
                    </button>
                    <button onClick={() => { setCatEditTarget(null); setCatEditNewEn(''); setCatEditNewAr(''); setCatEditMsg(''); }} style={{ padding: '8px 14px', borderRadius: 7, background: '#f2f0eb', color: '#666', border: '1.5px solid var(--sedra-line)', cursor: 'pointer', fontSize: 13 }}>
                      {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                    </button>
                  </div>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                {dbCategories.map(c => {
                  const isActive  = catFilter === c.en;
                  const isEditing = catEditTarget?.en === c.en;
                  const label     = lang === 'ar' ? (c.ar || c.en) : c.en;
                  const missingAr = !c.ar;
                  return (
                    <div key={c.en} style={{ padding: '11px 13px', borderRadius: 9, border: '2px solid ' + (isEditing ? 'var(--sedra-sage-ink)' : isActive ? 'var(--sedra-forest-soft)' : 'var(--sedra-line)'), background: isEditing ? 'var(--sedra-sage-wash)' : isActive ? 'var(--sedra-olive)' : '#F7F5F0', display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
                      <span style={{ cursor: 'pointer', color: isActive ? '#fff' : 'var(--sedra-sage-ink)', display: 'flex' }} onClick={() => setCatFilter(isActive ? 'all' : c.en)}><CategoryIcon category={c.en} size={20} /></span>
                      <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setCatFilter(isActive ? 'all' : c.en)}>
                        <div style={{ fontWeight: 700, fontSize: 12.5, color: isActive ? '#fff' : 'var(--sedra-forest-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {label}{missingAr && <span style={{ marginInlineStart: 5, fontSize: 9, background: 'var(--status-warn-bg)', color: 'var(--status-warn)', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>AR?</span>}
                        </div>
                        <div style={{ fontSize: 11, color: isActive ? '#fff' : '#999' }}>{c.product_count} {lang === 'ar' ? 'منتج' : 'products'}</div>
                      </div>
                      <button onClick={e => { e.stopPropagation(); setCatEditTarget(c); setCatEditNewEn(c.en); setCatEditNewAr(c.ar || ''); setCatEditMsg(''); }}
                        style={{ padding: '3px 7px', borderRadius: 5, background: isActive ? 'rgba(255,255,255,0.2)' : '#e8e4dc', color: isActive ? '#fff' : 'var(--sedra-sage-ink)', border: 'none', cursor: 'pointer', flexShrink: 0, display: 'flex' }}><Icon name="Pencil" size={12} /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {selectedIds.size > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', background: 'var(--sedra-forest-soft)', borderRadius: 9, padding: '10px 16px', marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="Check" size={14} /> {selectedIds.size} {lang === 'ar' ? 'محدد' : 'selected'}</span>
              <select value={bulkCat} onChange={e => setBulkCat(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 6, border: 'none', fontSize: 13, flex: 1, minWidth: 180 }}>
                <option value="">-- {lang === 'ar' ? 'تعيين فئة' : 'Assign category'} --</option>
                {dbCategories.map(c => (
                  <option key={c.en} value={c.en}>{lang === 'ar' ? (c.ar || c.en) : c.en} ({c.product_count})</option>
                ))}
              </select>
              <button onClick={applyBulkCat} disabled={!bulkCat}
                style={{ padding: '7px 16px', borderRadius: 6, background: bulkCat ? 'var(--sedra-olive)' : '#555', color: '#fff', border: 'none', cursor: bulkCat ? 'pointer' : 'default', fontWeight: 700, fontSize: 13 }}>
                {lang === 'ar' ? 'تطبيق' : 'Apply'}
              </button>
              <button onClick={() => setSelectedIds(new Set())}
                style={{ padding: '7px 12px', borderRadius: 6, background: 'transparent', color: '#ccc', border: '1px solid #666', cursor: 'pointer', fontSize: 12 }}>
                {lang === 'ar' ? 'إلغاء' : 'Clear'}
              </button>
              <button onClick={bulkDeactivate}
                style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--status-danger)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon name="Ban" size={14} />
                <span>{lang === 'ar' ? `إلغاء تنشيط (${selectedIds.size})` : `Deactivate (${selectedIds.size})`}</span>
              </button>
            </div>
          )}

          {busy && (
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
                {[0,1,2,3,4,5].map(i => <SkeletonAdminCard key={i} />)}
              </div>
            </div>
          )}

          {!busy && filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#aaa', padding: 40 }}>{t.noProducts}</div>
          ) : !busy && isMobile ? (
            /* ── MOBILE: flat card list ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {filtered.map(p => {
                const inactive = p.is_active === false;
                return (
                  <div key={p.id} style={{ background: inactive ? '#fafafa' : (selectedIds.has(p.id) ? '#f0f4ed' : '#fff'), borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', display: 'flex', gap: 12, alignItems: 'center', border: '2px solid ' + (selectedIds.has(p.id) ? 'var(--sedra-forest-soft)' : 'transparent'), opacity: inactive ? 0.55 : 1 }}>
                    <input type="checkbox" checked={selectedIds.has(p.id)} onChange={ev => { setSelectedIds(s => { const ns = new Set(s); ev.target.checked ? ns.add(p.id) : ns.delete(p.id); return ns; }); }} style={{ flexShrink: 0, width: 16, height: 16, accentColor: 'var(--sedra-forest-soft)', cursor: 'pointer' }} />
                    {p.image ? <img src={p.image} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} /> : <div style={{ width: 52, height: 52, background: 'var(--sedra-line)', borderRadius: 8, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--sedra-forest-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {p.name_en}
                        {inactive && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--status-danger-bg)', color: 'var(--status-danger)', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>INACTIVE</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--sedra-sage-ink)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}><CategoryIcon category={p.category} size={12} /> {catLabel(p.category, lang)}</div>
                      <div style={{ fontSize: 12, color: '#aaa' }}>{p.sku}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--sedra-forest-soft)', marginTop: 2 }}>{fmt(p.price)}</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => setForm({ ...p })} style={{ ...btnStyle('#6c8ebf'), padding: '6px 10px', display: 'flex' }}><Icon name="Pencil" size={13} /></button>
                      <button onClick={() => del(p)} style={{ ...btnStyle(inactive ? 'var(--sedra-olive)' : 'var(--status-danger)'), padding: '6px 10px', display: 'flex' }}>
                        <Icon name={inactive ? 'RotateCcw' : 'Ban'} size={13} />
                      </button>
                      {inactive && (
                        <button onClick={() => hardDel(p)} title={lang === 'ar' ? 'حذف نهائي' : 'Permanently delete'} style={{ ...btnStyle('#7a1e1e'), padding: '6px 10px', display: 'flex' }}><Icon name="Trash2" size={13} /></button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : !busy && (
            /* ── DESKTOP: grouped by category ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {Object.entries(grouped).map(([cat, items]) => {
                const isOpen = collapsed[cat] !== true;
                return (
                  <div key={cat} style={{ borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                    <div onClick={() => setCollapsed(c => ({ ...c, [cat]: !c[cat] }))}
                      style={{ background: 'var(--sedra-forest-soft)', color: '#fff', padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}><CategoryIcon category={cat} size={15} /> {catLabel(cat, lang)}</span>
                        <span style={{ background: 'var(--sedra-sage-ink)', color: '#fff', borderRadius: 10, padding: '2px 9px', fontSize: 12, fontWeight: 600 }}>{items.length}</span>
                      </div>
                      <span style={{ fontSize: 14, opacity: 0.7, transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
                    </div>
                    {isOpen && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff' }}>
                        <thead>
                          <tr style={{ background: 'var(--sedra-cream)' }}>
                            {['', t.nameEn, t.sku, t.price, t.minQty, t.cartonQty, lang==='ar'?'المخزون':'Stock', lang==='ar'?'الحالة':'Status', ''].map((h, i) => (
                              <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#666', whiteSpace: 'nowrap' }}>
                                {i === 8
                                  ? <input type="checkbox" title="Select all in group"
                                      checked={items.length > 0 && items.every(p => selectedIds.has(p.id))}
                                      onChange={ev => { const ns = new Set(selectedIds); items.forEach(p => ev.target.checked ? ns.add(p.id) : ns.delete(p.id)); setSelectedIds(ns); }}
                                      style={{ width: 14, height: 14, accentColor: 'var(--sedra-forest-soft)', cursor: 'pointer' }} />
                                  : h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((p, i) => {
                            const inactive = p.is_active === false;
                            return (
                              <tr key={p.id} style={{ borderTop: '1px solid var(--sedra-line)', background: inactive ? '#fafafa' : (selectedIds.has(p.id) ? '#eef4ea' : (i%2===0?'#fff':'#fdfbf8')), opacity: inactive ? 0.6 : 1, transition: 'background 0.15s' }}>
                                <td style={{ padding: '8px 14px' }}>
                                  {p.image ? <img src={p.image} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6 }} /> : <div style={{ width: 40, height: 40, background: 'var(--sedra-line)', borderRadius: 6 }} />}
                                </td>
                                <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--sedra-forest-soft)' }}>{lang === 'ar' && p.name_ar ? p.name_ar : p.name_en}</td>
                                <td style={{ padding: '8px 14px', color: '#888', fontSize: 13 }}>{p.sku}</td>
                                <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--sedra-sage-ink)', cursor: 'pointer' }}
                                  onClick={() => { if (editPriceId !== p.id) { setEditPriceId(p.id); setEditPrice(String(p.price)); } }}>
                                  {editPriceId === p.id
                                    ? <input autoFocus type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} onBlur={() => savePrice(p.id)} onKeyDown={e => { if (e.key==='Enter') savePrice(p.id); if (e.key==='Escape') setEditPriceId(null); }} onClick={e => e.stopPropagation()} style={{ width: 80, padding: '3px 6px', borderRadius: 5, border: '1.5px solid var(--sedra-sage-ink)', fontSize: 13, fontWeight: 600 }} />
                                    : <span title="Click to edit" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>{fmt(p.price)} <Icon name="Pencil" size={9} style={{ opacity: 0.45 }} /></span>}
                                </td>
                                <td style={{ padding: '8px 14px', color: '#888' }}>{p.min_qty}</td>
                                <td style={{ padding: '8px 14px', color: '#888' }}>{p.carton_qty ? <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--sedra-sage-ink)' }}>{p.carton_qty} {lang==='ar'?'قطعة':'pcs'}</span> : '—'}</td>
                                <td style={{ padding: '8px 14px', cursor: 'pointer' }}
                                  onClick={() => { if (editStockId !== p.id) { setEditStockId(p.id); setEditStock(String(p.stock ?? 0)); } }}>
                                  {editStockId === p.id
                                    ? <input autoFocus type="number" min="0" value={editStock} onChange={e => setEditStock(e.target.value)} onBlur={() => saveStock(p.id)} onKeyDown={e => { if (e.key==='Enter') saveStock(p.id); if (e.key==='Escape') setEditStockId(null); }} onClick={e => e.stopPropagation()} style={{ width: 72, padding: '3px 6px', borderRadius: 5, border: '1.5px solid var(--sedra-sage-ink)', fontSize: 13, fontWeight: 600 }} />
                                    : <span title={lang==='ar'?'انقر لتعديل المخزون':'Click to edit stock'} style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:8, background: p.stock===0?'var(--status-danger-bg)':p.stock<10?'var(--status-warn-bg)':'var(--status-success-bg)', color: p.stock===0?'var(--status-danger)':p.stock<10?'var(--status-warn-dark)':'var(--status-success)' }}>
                                        {p.stock===0?(lang==='ar'?'نفذ':'Out'):p.stock<10?(lang==='ar'?'قليل':'Low'):p.stock+(lang==='ar'?' وحدة':' units')} <Icon name="Pencil" size={9} style={{ opacity: 0.45 }} />
                                      </span>}
                                </td>
                                {/* Status badge */}
                                <td style={{ padding: '8px 14px' }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: inactive ? 'var(--status-danger-bg)' : 'var(--status-success-bg)', color: inactive ? 'var(--status-danger)' : 'var(--status-success)' }}>
                                    {inactive ? (lang==='ar'?'غير نشط':'Inactive') : (lang==='ar'?'نشط':'Active')}
                                  </span>
                                </td>
                                <td style={{ padding: '8px 14px' }}>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    <input type="checkbox" checked={selectedIds.has(p.id)} onChange={ev => { setSelectedIds(s => { const ns = new Set(s); ev.target.checked ? ns.add(p.id) : ns.delete(p.id); return ns; }); }} style={{ width: 14, height: 14, accentColor: 'var(--sedra-forest-soft)', cursor: 'pointer', marginRight: 4 }} />
                                    <button onClick={() => setForm({ ...p })} style={{ ...btnStyle('#6c8ebf'), display: 'flex' }}><Icon name="Pencil" size={13} /></button>
                                    <button onClick={() => del(p)} title={inactive ? (lang==='ar'?'استعادة':'Restore') : (lang==='ar'?'إلغاء التنشيط':'Deactivate')}
                                      style={{ ...btnStyle(inactive ? 'var(--sedra-olive)' : '#e07a2a'), display: 'flex' }}>
                                      <Icon name={inactive ? 'RotateCcw' : 'Ban'} size={13} />
                                    </button>
                                    {inactive && (
                                      <button onClick={() => hardDel(p)} title={lang==='ar'?'حذف نهائي':'Permanently delete'} style={{ ...btnStyle('#7a1e1e'), display: 'flex' }}><Icon name="Trash2" size={13} /></button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
